import { SocioEmpresas } from "@/components/SocioEmpresas";
import { Button } from "@/components/ui/button";
import { normalizePageSize } from "@/lib/filters";
import { UI } from "@/lib/labels";
import { ArrowLeft } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

interface LocationState {
  from?: string;
}

function buildParams(
  nome: string,
  documento: string | null,
  page: number,
  pageSize: number,
): URLSearchParams {
  const params = new URLSearchParams();
  if (nome) {
    params.set("nome", nome);
  }
  if (documento) {
    params.set("documento", documento);
  }
  params.set("page", String(page));
  params.set("pageSize", String(pageSize));
  return params;
}

/** Página que lista todas as empresas em que uma pessoa/entidade é sócia. */
export function SocioPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from;

  const paramsString = searchParams.toString();
  const nome = (searchParams.get("nome") ?? "").trim();
  const documento = (searchParams.get("documento") ?? "").trim() || null;
  const pageParam = Number(searchParams.get("page"));
  const page = Number.isFinite(pageParam) && pageParam >= 1 ? Math.floor(pageParam) : 1;
  const pageSize = normalizePageSize(Number(searchParams.get("pageSize")));

  const handlePageChange = useCallback(
    (nextPage: number) => {
      setSearchParams(buildParams(nome, documento, nextPage, pageSize));
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [nome, documento, pageSize, setSearchParams],
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setSearchParams(buildParams(nome, documento, 1, size));
    },
    [nome, documento, setSearchParams],
  );

  const goBack = useCallback(() => {
    if (from) {
      navigate(from);
    } else {
      navigate(-1);
    }
  }, [from, navigate]);

  const selfUrl = useMemo(() => `/socio?${paramsString}`, [paramsString]);

  return (
    <div className="space-y-5">
      <Button variant="ghost" onClick={goBack}>
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {UI.actions.back}
      </Button>

      <SocioEmpresas
        nome={nome}
        documento={documento}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        from={selfUrl}
        invalidAction={
          <Button variant="outline" size="sm" onClick={goBack}>
            {UI.actions.back}
          </Button>
        }
      />
    </div>
  );
}
